import dynamic from 'next/dynamic';

import { Carousel, CarouselContent, CarouselItem } from '@natu/ui/Carousel';
import { cn } from '@natu/utils/cn';
import { isArrayWithLength } from '@natu/utils/isArrayWithLength';

import {
  type BlokItem,
  type BorderRadius,
  DynamicRender,
  type SBProps,
  type Spacing,
  resolveStoryblokStyles,
  sbEditable,
} from '../../../../utils';

const CarouselGradient = dynamic(() =>
  import('@natu/ui/Carousel').then(mod => mod.CarouselGradient),
);

const CarouselNext = dynamic(() => import('@natu/ui/Carousel').then(mod => mod.CarouselNext));

const CarouselPrevious = dynamic(() =>
  import('@natu/ui/Carousel').then(mod => mod.CarouselPrevious),
);

const CarouselDots = dynamic(() => import('@natu/ui/Carousel').then(mod => mod.CarouselDots));

type SliderSize = 'full' | '2/3' | '1/2' | '1/3' | '1/4' | '3/4' | '1/5';

const sliderSizeMobile: Record<SliderSize, string> = {
  full: 'basis-full',
  '2/3': 'basis-2/3',
  '3/4': 'basis-3/4',
  '1/2': 'basis-1/2',
  '1/3': 'basis-1/3',
  '1/4': 'basis-1/4',
  '1/5': 'basis-1/5',
};
const sliderSizeTablet: Record<SliderSize, string> = {
  full: 'md:basis-full',
  '2/3': 'md:basis-2/3',
  '3/4': 'md:basis-3/4',
  '1/2': 'md:basis-1/2',
  '1/3': 'md:basis-1/3',
  '1/4': 'md:basis-1/4',
  '1/5': 'md:basis-1/5',
};
const sliderSizeDesktop: Record<SliderSize, string> = {
  full: 'lg:basis-full',
  '2/3': 'lg:basis-2/3',
  '3/4': 'lg:basis-3/4',
  '1/2': 'lg:basis-1/2',
  '1/3': 'lg:basis-1/3',
  '1/4': 'lg:basis-1/4',
  '1/5': 'lg:basis-1/5',
};

interface SBCarouselProps {
  items?: BlokItem[];
  loop?: boolean;
  startIndex?: string;
  slidesToScroll?: string;
  paginationMarginTop?: Spacing;
  paginationMarginTopTablet?: Spacing;
  paginationMarginTopDesktop?: Spacing;
  paginationPosition?: 'left' | 'center' | 'right';
  marginTopMobile?: Spacing;
  marginTopTablet?: Spacing;
  marginTopDesktop?: Spacing;
  marginBottomMobile?: Spacing;
  marginBottomTablet?: Spacing;
  marginBottomDesktop?: Spacing;
  paginationType?: 'dots' | 'arrow' | 'dotsArrow';
  paginationTheme?: 'white' | 'primary';
  itemsSizeMobile?: SliderSize;
  itemsSizeTablet?: SliderSize;
  itemsSizeDesktop?: SliderSize;
  spaceBetweenMobile?: Spacing;
  spaceBetweenTablet?: Spacing;
  spaceBetweenDesktop?: Spacing;
  carouselType?: 'default' | 'top-bottom';
  autoplay?: boolean;
  autoplayDelay?: string;
  disableCustomCursor?: boolean;
  enableBlur?: boolean;
  borderRadiusMobile?: BorderRadius;
  borderRadiusTablet?: BorderRadius;
  borderRadiusDesktop?: BorderRadius;
  sendEvent?: boolean;
  event_carousel_type?: string;
}

export const SBCarousel = ({ blok }: SBProps<SBCarouselProps>) => {
  const {
    _uid,
    items,
    loop,
    startIndex,
    slidesToScroll,
    paginationMarginTop,
    paginationMarginTopTablet,
    paginationMarginTopDesktop,
    paginationPosition = 'center',
    paginationTheme = 'primary',
    paginationType,
    marginTopMobile,
    marginTopTablet,
    marginTopDesktop,
    marginBottomMobile,
    marginBottomTablet,
    marginBottomDesktop,
    itemsSizeMobile,
    itemsSizeTablet,
    itemsSizeDesktop,
    spaceBetweenMobile,
    spaceBetweenTablet,
    spaceBetweenDesktop,
    carouselType,
    autoplay,
    autoplayDelay,
    borderRadiusMobile,
    borderRadiusTablet,
    borderRadiusDesktop,
    disableCustomCursor,
    enableBlur,
    sendEvent,
    event_carousel_type,
  } = blok;

  if (!isArrayWithLength(items)) {
    return null;
  }

  const wrapperClassName = resolveStoryblokStyles({
    mt: marginTopMobile,
    mtTablet: marginTopTablet,
    mtDesktop: marginTopDesktop,
  });

  const paginationClassName = resolveStoryblokStyles({
    mt: paginationMarginTop,
    mtTablet: paginationMarginTopTablet,
    mtDesktop: paginationMarginTopDesktop,
    mb: marginBottomMobile,
    mbTablet: marginBottomTablet,
    mbDesktop: marginBottomDesktop,
    className: cn(
      'flex items-center gap-3 sm:gap-8',
      paginationPosition === 'left' && 'justify-start',
      paginationPosition === 'center' && 'justify-center',
      paginationPosition === 'right' && 'justify-end',
    ),
  });

  const mobileVariant = itemsSizeMobile ? sliderSizeMobile[itemsSizeMobile] : '';
  const tabletVariant = itemsSizeTablet ? sliderSizeTablet[itemsSizeTablet] : mobileVariant;
  const desktopVariant = itemsSizeDesktop ? sliderSizeDesktop[itemsSizeDesktop] : tabletVariant;

  const contentClassName = resolveStoryblokStyles({
    mlMinus: spaceBetweenMobile,
    mlMinusTablet: spaceBetweenTablet,
    mlMinusDesktop: spaceBetweenDesktop,
  });

  const sliderItemClassName = resolveStoryblokStyles({
    pl: spaceBetweenMobile,
    plTablet: spaceBetweenTablet,
    plDesktop: spaceBetweenDesktop,
    className: cn(
      mobileVariant,
      tabletVariant,
      desktopVariant,
      carouselType === 'top-bottom' && 'odd:lg:pb-12 even:lg:pt-12',
    ),
  });

  const roundedWrapperClassName = resolveStoryblokStyles({
    rounded: borderRadiusMobile,
    roundedTablet: borderRadiusTablet,
    roundedDesktop: borderRadiusDesktop,
    className: cn(
      borderRadiusDesktop || borderRadiusTablet || borderRadiusMobile ? 'overflow-clip' : '',
    ),
  });

  return (
    <Carousel
      opts={{
        loop,
        startIndex: startIndex ? Number(startIndex) : undefined,
        slidesToScroll: slidesToScroll ? Number(slidesToScroll) : undefined,
      }}
      disableCustomCursor={disableCustomCursor}
      autoplay={autoplay}
      autoplayDelay={Number(autoplayDelay || '2000')}
      className={wrapperClassName}
      event={sendEvent ? 'E_click_carousel' : undefined}
      eventProps={{ event_carousel_type }}
      {...sbEditable(blok)}
    >
      <div className="relative">
        <CarouselContent className={cn(contentClassName)}>
          {items?.map(item => (
            <CarouselItem key={item._uid} className={sliderItemClassName}>
              <div className={roundedWrapperClassName}>
                <DynamicRender data={item} />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        {enableBlur && <CarouselGradient />}
      </div>
      {paginationType && (
        <nav className={cn(paginationClassName, 'empty:hidden')}>
          {(paginationType === 'arrow' || paginationType === 'dotsArrow') && (
            <>
              <CarouselPrevious theme={paginationTheme || 'primary'} />
              <CarouselNext theme={paginationTheme || 'primary'} className="order-1" />
            </>
          )}
          {(paginationType === 'dots' || paginationType === 'dotsArrow') && (
            <CarouselDots layoutID={_uid} theme={paginationTheme || 'primary'} />
          )}
        </nav>
      )}
    </Carousel>
  );
};
